/**
 * Remix button visibility for Malleable Content. Canonical deny list and show rules.
 * Use with block-controls activeNode (or a full PM node) and these filters in both edit and view.
 */

import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	REMIX_MENU_COHORT_PARAM,
	REMIX_MENU_CONTROL_COHORT,
	REMIX_MENU_EXPERIMENT,
	REMIX_MENU_INLINE_DROPDOWN_COHORT,
	isAllowedMBEExtensionForRemix,
	isRemixRestrictedNodeType,
} from '@atlassian/malleable-ui-components/remix/remix-config';

/** Node types that get the Ask Rovo button on their floating toolbar (media nodes). */
export const ASK_ROVO_MEDIA_NODE_TYPES: readonly string[] = ['mediaSingle', 'media'] as const;

export const isConsideredEmptyForRemix = (node?: PMNode): boolean => {
	if (!node) {
		return false;
	}
	// Aya's principle:
	// Empty anything should not have the Remix button shown
	// Once there is text or something in the element, we can show it
	// NOTE: Currently, we are ignoring "something" because Remix can't handle only emoji, status, etc.
	return node.textContent.trim().length === 0;
};

export const REMIX_FLOATING_TOOLBAR_TEST_ID = 'editor-floating-toolbar__remix-button';

/** Popper [skidding, distance] offset shared by both Editor Remix toolbar entry points. */
export const REMIX_TOOLBAR_POPUP_OFFSET: [number, number] = [0, 8];

/**
 * Marker class used purely to locate the rendered Remix floating-toolbar button so the inline
 * dropdown can be anchored to it. Carries no styles, and is only applied for the inline-dropdown
 * cohort.
 */
export const REMIX_FLOATING_TOOLBAR_ANCHOR_CLASS = 'malleable-ui-remix-floating-toolbar-anchor';

/**
 * `cc-ce-maui-remix-menu-multivariant`: only the inline-dropdown cohort replaces the central
 * Remix modal with a dropdown anchored to the entry point. Every other assignment (control,
 * off, other variants) keeps the existing experience untouched.
 *
 * Read without exposure — the exposure is fired by `RemixContainer` when the menu is shown.
 */
export const isRemixInlineDropdownVariant = (): boolean =>
	UNSAFE_expValNoExposure(
		REMIX_MENU_EXPERIMENT,
		REMIX_MENU_COHORT_PARAM,
		REMIX_MENU_CONTROL_COHORT,
	) === REMIX_MENU_INLINE_DROPDOWN_COHORT;

/**
 * MBE extension keys that are allowed to use Remix in Confluence.
 * The allow list is owned by the product layer so product-specific
 * decisions stay out of platform code.
 *
 * Already gated by the existing `cc-maui` experiment (controls Remix
 * visibility) and `confluence_native_tabs_experiment` (controls
 * whether native tabs nodes exist in the editor).
 */
const REMIX_MBE_ALLOW_LIST: ReadonlyArray<string> = ['native-tabs'];

/**
 * Extension keys that are allowed to use Remix in Confluence, no empty check.
 * The allow list is owned by the product layer so product-specific
 * decisions stay out of platform code.
 *
 * Emptiness checks don't make sense for extensions like native embeds because
 * they don't have text content.
 */
const REMIX_NO_EMPTY_CHECK_EXTENSION_ALLOW_LIST: ReadonlySet<string> = new Set([
	'native-embed:page',
]);

/**
 * Check if the extension key is allowed to use Remix, gated by feature flags.
 */
const isAllowedExtensionForRemixNoEmptyCheck = (extensionKey: string): boolean =>
	REMIX_NO_EMPTY_CHECK_EXTENSION_ALLOW_LIST.has(extensionKey) &&
	fg('maui_remixable_page_native_embeds');

/**
 * Reusable filter for remix button visibility. Use with block-controls activeNode
 * (nodeType / rootNodeType) or a full PM node in both edit and view.
 *
 * - Deny-list is always applied when we have a type (never show Remix on media, extensions, etc.).
 * - multiBodiedExtension nodes are an exception: they are allowed when their extensionKey is in
 *   the MBE allow list (e.g. native tabs), so Remix is scoped to specific MBE variants only.
 * - When a full node is provided, we also require the node to be non-empty (no Remix on empty blocks).
 * - MBE nodes are an exception: they always show the Remix button (disabled when empty).
 * - When only nodeType is available (e.g. from activeNode in view mode), we only apply the deny-list.
 */
export const shouldShowRemixForSelectionContext = (context: {
	extensionKey?: string;
	node?: PMNode;
	nodeType?: string;
}): boolean => {
	const nodeType = context.node?.type?.name ?? context.nodeType;
	if (nodeType === undefined) {
		return false;
	}
	// Allow specific MBE variants (e.g. native tabs) even though the generic
	// multiBodiedExtension type is in the deny list.
	if (nodeType === 'multiBodiedExtension' && context.node) {
		const extensionKey = context.node.attrs?.extensionKey;
		if (isAllowedMBEExtensionForRemix(extensionKey, REMIX_MBE_ALLOW_LIST)) {
			return true;
		}
		return false;
	}
	const extensionKey = context.node?.attrs?.extensionKey ?? context.extensionKey ?? '';
	if (nodeType === 'extension' && isAllowedExtensionForRemixNoEmptyCheck(extensionKey)) {
		return true;
	}
	if (isRemixRestrictedNodeType(nodeType)) {
		return false;
	}
	return !isConsideredEmptyForRemix(context.node);
};
